import '../css/style.css';
import { NoticeManager } from './lib/NoticeManager';

/**
 * Maps a DOM sentinel element ID to the dynamic import for its calculator module.
 * Each calculator is only loaded when the page contains the relevant element,
 * keeping the main bundle slim and avoiding unnecessary work on unrelated pages.
 */
const calculatorRegistry: Record<
    string,
    () => Promise<{ default?: () => void } & Record<string, () => void>>
> = {
    'grand-total': () =>
        import('./calculators/quoteCalculator').then((m) => ({
            initializeQuoteCalculator: m.initializeQuoteCalculator,
        })),
    'hourly-rate-result': () =>
        import('./calculators/hourlyRateCalculator').then((m) => ({
            initializeHourlyRateCalculator: m.initializeHourlyRateCalculator,
        })),
    'vat-calculator': () =>
        import('./calculators/vatCalculator').then((m) => ({
            initializeVATCalculator: m.initializeVATCalculator,
        })),
    'chargeout-rate-result': () =>
        import('./calculators/chargeOutRateCalculator').then((m) => ({
            initializeChargeOutRateCalculator:
                m.initializeChargeOutRateCalculator,
        })),
    'materials-result-value': () =>
        import('./calculators/materialsMarkupCalculator').then((m) => ({
            initializeMaterialsMarkupCalculator:
                m.initializeMaterialsMarkupCalculator,
        })),
    'profit-result-card': () =>
        import('./calculators/jobProfitabilityCalculator').then((m) => ({
            initializeJobProfitabilityCalculator:
                m.initializeJobProfitabilityCalculator,
        })),
    'reverse-charge-verdict': () =>
        import('./calculators/reverseChargeChecker').then((m) => ({
            initializeReverseChargeChecker: m.initializeReverseChargeChecker,
        })),
    'cis-deduction-calculator': () =>
        import('./calculators/cisDeductionCalculator').then((m) => ({
            initializeCisDeductionCalculator:
                m.initializeCisDeductionCalculator,
        })),
    'pricing-picker-verdict': () =>
        import('./calculators/pricingTypePicker').then((m) => ({
            initializePricingTypePicker: m.initializePricingTypePicker,
        })),
    'team-cost-calculator': () =>
        import('./calculators/teamCostCalculator').then((m) => ({
            initializeTeamCostCalculator: m.initializeTeamCostCalculator,
        })),
    'solar-battery-calculator': () =>
        import('./calculators/solarBatteryCalculator').then((m) => ({
            initializeSolarBatteryCalculator:
                m.initializeSolarBatteryCalculator,
        })),
    'heating-pipe-size-calculator': () =>
        import('./calculators/heatingPipeSizeCalculator').then((m) => ({
            initializeHeatingPipeSizeCalculator:
                m.initializeHeatingPipeSizeCalculator,
        })),
    'excavation-digs': () =>
        import('./calculators/excavationCalculator').then((m) => ({
            initializeExcavationCalculator: m.initializeExcavationCalculator,
        })),
    'flooring-rooms': () =>
        import('./calculators/flooringCalculator').then((m) => ({
            initializeFlooringCalculator: m.initializeFlooringCalculator,
        })),
    'cis-statement-generator': () =>
        import('./calculators/cisStatementGenerator').then((m) => ({
            initializeCisStatementGenerator: m.initializeCisStatementGenerator,
        })),
    'tool-filters': () =>
        import('./lib/toolFilters').then((m) => ({
            initializeToolFilters: m.initializeToolFilters,
        })),
    'rooms-container': () =>
        import('./calculators/heatLossCalculator').then((m) => ({
            initializeHeatLossCalculator: m.initializeHeatLossCalculator,
        })),
};

class ProWorksPublic {
    private noticeManager: NoticeManager;

    constructor() {
        this.noticeManager = new NoticeManager();
        this.initializeMobileMenu();
        this.initializeSmoothScrolling();
        this.initializeScrollEffects();
        this.initializeCalculators();
        this.noticeManager.initialize();
    }

    /**
     * Conditionally load calculator modules based on which DOM elements exist.
     * Uses Vite dynamic imports so each calculator is a separate chunk
     * that's only fetched when needed.
     */
    private initializeCalculators(): void {
        for (const [sentinelId, loader] of Object.entries(calculatorRegistry)) {
            if (document.getElementById(sentinelId)) {
                loader().then((module) => {
                    // Call the first exported function from the module
                    const initFunction = Object.values(module).find(
                        (value): value is () => void =>
                            typeof value === 'function'
                    );
                    initFunction?.();
                });
            }
        }
    }

    private initializeMobileMenu(): void {
        const button = document.getElementById('mobile-menu-button');
        const menu = document.getElementById('mobile-menu');

        if (!button || !menu) return;

        const closeMenu = () => {
            button.classList.remove('rotate-90');
            menu.classList.remove('max-h-96');
            menu.classList.add('max-h-0');
            button.setAttribute('aria-expanded', 'false');
        };

        const openMenu = () => {
            menu.classList.remove('max-h-0');
            menu.classList.add('max-h-96');
            button.setAttribute('aria-expanded', 'true');
        };

        button.addEventListener('click', () => {
            button.classList.toggle('rotate-90');

            if (menu.classList.contains('max-h-0')) {
                openMenu();
            } else {
                closeMenu();
            }
        });

        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (!menu.contains(target) && !button.contains(target)) {
                closeMenu();
            }
        });

        menu.querySelectorAll('a').forEach((link) => {
            link.addEventListener('click', closeMenu);
        });
    }

    private initializeSmoothScrolling(): void {
        document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
            anchor.addEventListener('click', (e) => {
                e.preventDefault();
                const href = anchor.getAttribute('href');
                if (href && href !== '#') {
                    document.querySelector(href)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            });
        });
    }

    private initializeScrollEffects(): void {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-fade-in');
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        );

        document
            .querySelectorAll('article, .bg-gradient-to-r')
            .forEach((el) => {
                observer.observe(el);
            });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new ProWorksPublic();
});
